import type {
  AppliedInstruction,
  AppliedTaskChange,
  ChangeEntry,
  ChangeTarget,
  ChangeWriters,
  ChangeWritersOptions,
  InstructResponse,
  InstructTaskPayload,
  InstructionContext,
  InstructionHistory,
  TaskFields,
} from '@/components/session/note-instructions/noteInstructions.types';
import { tasksQueryKey } from '@/hooks/useTasks';
import type { Document } from '@/lib/mockData';
import {
  createInstructionTask,
  deleteInstructionTask,
  instructionTaskExtraData,
  requestSourceExtraData,
  updateInstructionTask,
} from '@/lib/noteInstructions';

const MAX_TASKS = 50;

/** The note or a targeted task changed while the instruction was in flight. */
export class StaleContextError extends Error {}

export const EMPTY_HISTORY: InstructionHistory = { entries: [] };

export const taskDocId = (dbId: number) => `db-${dbId}`;

const isDraft = (task: Document | undefined): task is Document => task?.status === 'Drög';

/** Only draft tasks are sent: a task the clinician confirmed is locked and must not be changed. */
export const toTaskPayload = (tasks: Document[]): InstructTaskPayload[] =>
  tasks
    .filter((task) => task.dbId != null && isDraft(task))
    .slice(0, MAX_TASKS)
    .map((task) => ({ id: String(task.dbId), type: task.type, title: task.title, details: task.details }));

const sameFields = (task: Document | undefined, fields: TaskFields) =>
  !!task && task.title === fields.title && task.details === fields.details;

const targetsOf = (entry: Pick<ChangeEntry, 'note' | 'taskChanges'>): ChangeTarget[] => [
  ...(entry.note ? (['note'] as const) : []),
  ...entry.taskChanges.map((change) => (change.kind === 'add' ? ('new_task' as const) : ('task_updated' as const))),
];

/** Throws StaleContextError when the note or any task the proposal updates changed (or was
 * confirmed) during the request, or when an update targets a task that is not a draft. */
export const assertFresh = (
  context: InstructionContext,
  response: InstructResponse,
  latest: { note: string; tasks: Document[] }
) => {
  if (latest.note !== context.note) throw new StaleContextError();
  for (const op of response.task_ops) {
    if (op.op !== 'update') continue;
    const original = context.tasks.find((task) => String(task.dbId) === op.task_id);
    const current = latest.tasks.find((task) => String(task.dbId) === op.task_id);
    if (!isDraft(original) || !isDraft(current) || !sameFields(current, original)) {
      throw new StaleContextError();
    }
  }
};

type TaskOp = InstructResponse['task_ops'][number];

/** Folds several updates to one task into the first, later fields on top, so each task gets one
 * change whose prev/next span the whole instruction. */
const mergeTaskUpdates = (ops: TaskOp[]): TaskOp[] => {
  const merged: TaskOp[] = [];
  const updateIndex = new Map<string, number>();
  for (const op of ops) {
    const index = op.op === 'update' ? updateIndex.get(op.task_id) : undefined;
    if (op.op === 'update' && index !== undefined) {
      const earlier = merged[index] as Extract<TaskOp, { op: 'update' }>;
      merged[index] = { ...earlier, title: op.title ?? earlier.title, details: op.details ?? earlier.details };
      continue;
    }
    if (op.op === 'update') updateIndex.set(op.task_id, merged.length);
    merged.push(op);
  }
  return merged;
};

/** Whether what this entry touched is still as it left it, so undoing it reverts only its own change. */
export const canUndo = (entry: ChangeEntry, note: string, tasks: Document[]) => {
  if (entry.note && note !== entry.note.next) return false;
  return entry.taskChanges.every((change) => {
    const task = tasks.find((candidate) => candidate.dbId === change.dbId);
    // Never revert or delete a task the clinician has since confirmed.
    if (task && !isDraft(task)) return false;
    return sameFields(task, change.kind === 'update' ? change.next : change.fields);
  });
};

/** Persists note/task changes and mirrors task writes into the session's tasks cache. Each task
 * write first re-checks the page: still this session, and the task still the draft it expects
 * (the clinician may have confirmed it while an earlier write was pending). */
export const changeWriters = ({
  queryClient,
  sessionId,
  patientId,
  persistNote,
  latest,
}: ChangeWritersOptions): ChangeWriters => {
  const setCachedTasks = (update: (prev: Document[]) => Document[]) =>
    queryClient.setQueryData<Document[]>(tasksQueryKey(sessionId), (prev = []) => update(prev));
  const assertSameSession = () => {
    if (latest().sessionId !== sessionId) throw new StaleContextError();
  };
  const currentTask = (dbId: number) => {
    assertSameSession();
    const task = latest().tasks.find((candidate) => candidate.dbId === dbId);
    if (task && !isDraft(task)) throw new StaleContextError();
    return task;
  };
  return {
    saveNote: (text) => persistNote(text, sessionId),
    createTask: async (change) => {
      assertSameSession();
      const extraData = instructionTaskExtraData(change.type, change.fields.details);
      const dbId = await createInstructionTask({
        ...change.fields,
        type: change.type,
        sessionId,
        patientId,
        extraData,
      });
      const doc: Document = {
        id: taskDocId(dbId),
        type: change.type,
        ...change.fields,
        status: 'Drög',
        dbId,
        extraData,
      };
      setCachedTasks((prev) => [...prev, doc]);
      return dbId;
    },
    patchTask: async (change, useNext) => {
      const fields = useNext ? change.next : change.prev;
      const task = currentTask(change.dbId);
      if (!sameFields(task, useNext ? change.prev : change.next)) throw new StaleContextError();
      const extraData = requestSourceExtraData(task?.extraData, fields.details);
      await updateInstructionTask(change.dbId, fields, extraData);
      setCachedTasks((prev) =>
        prev.map((cached) =>
          cached.dbId === change.dbId ? { ...cached, ...fields, ...(extraData && { extraData }) } : cached
        )
      );
    },
    deleteTask: async (dbId) => {
      currentTask(dbId);
      await deleteInstructionTask(dbId);
      setCachedTasks((prev) => prev.filter((task) => task.dbId !== dbId));
    },
  };
};

const toTaskChange = async (
  op: TaskOp,
  context: InstructionContext,
  writers: ChangeWriters
): Promise<AppliedTaskChange | null> => {
  if (op.op === 'add') {
    const change = { kind: 'add' as const, dbId: 0, type: op.type, fields: { title: op.title, details: op.details } };
    return { ...change, dbId: await writers.createTask(change) };
  }
  const task = context.tasks.find((candidate) => String(candidate.dbId) === op.task_id);
  if (!task?.dbId) return null;
  const prev = { title: task.title, details: task.details };
  const change = {
    kind: 'update' as const,
    dbId: task.dbId,
    prev,
    next: { title: op.title ?? prev.title, details: op.details ?? prev.details },
  };
  await writers.patchTask(change, true);
  return change;
};

/** Applies the proposal step by step; whatever persisted is recorded so it can be undone. */
export const applyResponse = async (
  context: InstructionContext,
  response: InstructResponse,
  sessionId: string,
  writers: ChangeWriters
): Promise<AppliedInstruction> => {
  let note: ChangeEntry['note'] = null;
  const taskChanges: AppliedTaskChange[] = [];
  let failed = false;
  try {
    if (response.note !== null && response.note !== context.note) {
      await writers.saveNote(response.note);
      note = { prev: context.note, next: response.note };
    }
    for (const op of mergeTaskUpdates(response.task_ops)) {
      const change = await toTaskChange(op, context, writers);
      if (change) taskChanges.push(change);
    }
  } catch {
    failed = true;
  }
  const entry: ChangeEntry = {
    id: crypto.randomUUID(),
    sessionId,
    summary: response.change_summary,
    targets: targetsOf({ note, taskChanges }),
    appliedAt: new Date(),
    note,
    taskChanges,
  };
  return { entry, failed };
};

/** Reverts one entry: the note back to before it, its task updates undone, its new tasks deleted. */
export const revertEntry = async (entry: ChangeEntry, writers: ChangeWriters) => {
  if (entry.note) await writers.saveNote(entry.note.prev);
  for (const change of [...entry.taskChanges].reverse()) {
    if (change.kind === 'update') await writers.patchTask(change, false);
    else await writers.deleteTask(change.dbId);
  }
};

export const appendEntry = (history: InstructionHistory, entry: ChangeEntry): InstructionHistory => ({
  entries: [...history.entries, { ...entry, applied: true }],
});

/** An undone change stays listed, marked as undone. */
export const markUndone = (history: InstructionHistory, entryId: string): InstructionHistory => ({
  entries: history.entries.map((candidate) =>
    candidate.id === entryId ? { ...candidate, applied: false } : candidate
  ),
});
